import Delegate from 'ftdomdelegate'
import gsap from 'gsap'
import {
  getSiblings
} from '../tools/siblings'

let is_mobile
window.addEventListener('breakpoint-change', function (e) {
  is_mobile = e.detail.breakpoint != 'md' && e.detail.breakpoint != 'lg' && e.detail.breakpoint != 'xl' && e.detail.breakpoint != '2xl' && e.detail.breakpoint != '3xl' ? true : false
})
const nodeListToArray = (nodeList, filter) => {
  var items = []

  for (var i = 0; i !== nodeList.length; ++i) {
    if (!filter || nodeList[i].matches(filter)) {
      items.push(nodeList[i])
    }
  }

  return items
}


const _defineProperty = (obj, key, value) => {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    })
  } else {
    obj[key] = value
  }
  return obj
}

const collection = {}

// Filter Section
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    const summaryElements = this.querySelectorAll('summary');
    this.addAccessibilityAttributes(summaryElements);

    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach(element => {
      element.setAttribute('role', 'button');
      element.setAttribute('aria-expanded', false);
      element.setAttribute('aria-controls', element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
      });
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });


    if (updateURLHash){
      FacetFiltersForm.updateURLHash(searchParams);
      
      console.log(searchParams)
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    // document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    var _collection38 = collection
    var tempElement = document.createElement('div')
    tempElement.innerHTML = html;
    // _collection38.collectionInnerElement.innerHTML = tempElement.querySelector('#ProductGridContainer').innerHTML;
    _collection38.collectionInnerElement.innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    document.dispatchEvent(new CustomEvent('theme:loading:end'))

    _collection38._setupAnimation(true)

    // We scroll to the top
    var elementOffset = _collection38.delegateElement.rootElement.querySelector('.collectionMain').getBoundingClientRect().top - parseInt(document.documentElement.style.getPropertyValue('--header-height'));

    if (elementOffset < 0) {
      window.scrollBy({
        top: elementOffset,
        behavior: 'smooth'
      })
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [{
      section: document.getElementById('product-grid').dataset.id,
    }]
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}
customElements.define('facet-remove', FacetRemove);


collection._setupAnimation = () => {
  collection.timeline = gsap.timeline()
  if (window.theme.showElementStaggering) {
    var productItems = nodeListToArray(collection.delegateElement.rootElement.querySelectorAll('.productList .productItemWrapper'))
    if (productItems) {
      collection.timeline.staggerFromTo(productItems, 0.6, {
        autoAlpha: 0,
        y: 25
      }, {
        autoAlpha: 1,
        y: 0
      }, 0.2)
    }
  } else {
    var productList = collection.delegateElement.rootElement.querySelector('.productList')
    if (productList) {
      collection.timeline.fromTo(productList, 0.6, {
        autoAlpha: 0,
        y: 25
      }, {
        autoAlpha: 1,
        y: 0
      })
    }
  }

}

collection._changeLayoutMode = (event, target) => {
  var _collection35 = collection

  var layoutType = target.getAttribute('data-grid-type'),
    newCount = parseInt(target.getAttribute('data-count'))

  // Otherwise we detect the mode, and change all classes
  var productList = collection.collectionInnerElement.querySelector('.productList')

  if (productList) {
    var previousCount = parseInt(productList.getAttribute('data-' + layoutType + '-count'))

    if (previousCount === newCount) {
      return // Nothing has changed so we just return to avoid reflow
    }

    productList.setAttribute('data-' + layoutType + '-count', newCount)

    if (layoutType === 'mobile') {
      productList.classList.remove(previousCount > 1 ? 'grid-cols-' + previousCount : 'grid-cols-1') // IE11 and lower does not support classList.replace
      productList.classList.add(newCount > 1 ? 'grid-cols-' + newCount : 'grid-cols-1')
    } else {
      var previousTabletCount = previousCount === 2 ? 2 : 3,
        newTabletCount = newCount === 2 ? 2 : 3

      if (_collection35.settings['filterPosition'] === 'drawer') {
        productList.classList.remove('lg:grid-cols-' + previousCount)
        productList.classList.add('lg:grid-cols-' + newCount)
      } else {
        productList.classList.remove('xl:grid-cols-' + previousCount)
        productList.classList.add('xl:grid-cols-' + newCount)
      }

      productList.classList.remove('sm:grid-cols-' + previousTabletCount)
      productList.classList.add('sm:grid-cols-' + newTabletCount)
    }
    lazySizes.autoSizer.checkElems()
  }

  target.classList.add('is-active')
  getSiblings({
    element: target
  })[0].classList.remove('is-active')

  collection._setupAnimation()

  // In order to prevent reflow and provide better user experience, we save into cart attributes (those are removed before the checkout
  // is submitted) the user choices so they are preserved on page reload, without the need to use JavaScript

  fetch('/cart/update.js', {
    body: JSON.stringify({
      attributes: _defineProperty({}, 'collection_' + layoutType + '_items_per_row', newCount)
    }),
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest' // collection is needed as currently there is a bug in Shopify that assumes this header
    }
  })
}

collection._commit = () => {
  console.log('_commit')
  var _collection37 = collection

  if (collection.currentTags.sort().join(',') !== collection.temporaryTags.sort().join(',')) {
    collection.currentTags = collection.temporaryTags.slice()
    collection._reloadProducts()
  }

  nodeListToArray(collection.delegateElement.rootElement.querySelectorAll('[data-action="reset-tags"]')).forEach(function (resetButton) {
    resetButton.style.display = _collection37.currentTags.length === 0 ? 'none' : 'block'
  })
}

collection._reloadProducts = () => {
  console.log('_reloadProducts')
  var _collection38 = collection

  var filterElement = collection.toolbarElement.querySelector('.collectionToolbar__item--filter')

  if (filterElement) {
    var filterElementCount = filterElement.querySelector('span')

    if (filterElementCount) {
      filterElement.removeChild(filterElementCount)
    }

    if (collection.currentTags.length === 0) {
      filterElement.classList.add('text--subdued')
    } else {
      filterElement.classList.remove('text--subdued')
      filterElement.innerHTML += '<span class="text--subdued">(' + collection.currentTags.length + ')</span>'
    }
  }

  // We also rewrite the URL if browser supports it
  if (history.replaceState) {
    console.log(history.replaceState)
    var tags = collection.currentTags.length > 0 ? collection.currentTags.join('+') : ''
    var newUrl = window.location.protocol + '//' + window.location.host + collection.settings['collectionUrl'] + '/' + tags + '?sort_by=' + collection.currentSortBy

    window.history.pushState({
      path: newUrl
    }, '', newUrl)
  }

  var formData = new FormData()
  formData.append('view', 'ajax')
  formData.append('sort_by', collection.currentSortBy)

  fetch(location.pathname + '?view=ajax&sort_by=' + collection.currentSortBy, {
    credentials: 'same-origin',
    method: 'GET'
  }).then(function (response) {
    response.text().then(function (content) {
      var tempElement = document.createElement('div')
      tempElement.innerHTML = content;

      _collection38.collectionInnerElement.innerHTML = tempElement.querySelector('.shopify-section').innerHTML;
      document.dispatchEvent(new CustomEvent('theme:loading:end'))

      _collection38._setupAnimation(true)

      // We scroll to the top
      var elementOffset = _collection38.delegateElement.rootElement.querySelector('.collectionMain').getBoundingClientRect().top - parseInt(document.documentElement.style.getPropertyValue('--header-height'));

      if (!is_mobile && _collection38.toolbarElement && _collection38.toolbarElement.clientHeight === 0) {
        elementOffset -= 50
      }

      if (elementOffset < 0) {
        window.scrollBy({
          top: elementOffset,
          behavior: 'smooth'
        })
      }
    })
  })
}

collection.delegateElement = new Delegate(document.querySelector('[data-section-type="collection"]'))
collection.collectionInnerElement = collection.delegateElement.rootElement.querySelector('.collectionInner__products')
collection.settings = JSON.parse(collection.delegateElement.rootElement.getAttribute('data-section-settings'))


//Mobile filter fix
var mobileOpener = document.querySelector('.mobile-facets__open-wrapper');
mobileOpener.addEventListener('click',(e)=>{
  console.log('clicked');
  if(mobileOpener.getAttribute('aria-expanded') === 'true'){
    console.log('open');
  } 
  else{
    console.log('close');
  }
})
